/**
 * Platform bootstrap — the unauthenticated call resolved before first paint that
 * supplies every **absolute** origin this console needs (`authOrigin`, `loginUrl`,
 * optional `payOrigin`). wsr-api is same-origin `apiBase()` (`'/api'`); only
 * saas-auth origins come from bootstrap. Platform FE contract §4.1/§5.
 *
 * Fetched with plain `fetch`, NOT the axios client in `src/api/client.ts` —
 * `client.ts` needs `authOrigin` to resolve base URLs, so routing bootstrap
 * through it would be circular.
 *
 * Stored in a module-scoped variable only — never localStorage, sessionStorage,
 * cookies, or a service worker cache (hard constraint). `getBootstrap()` throws if
 * called before resolution: that is a programming error the blocking
 * `BootstrapGate` makes impossible, and a throw surfaces it immediately instead of
 * silently producing `undefined` in a URL.
 */
import { apiBase } from './platform';

export interface PlatformBootstrap {
  productSlug: string;
  productName: string;
  /** Complete URL. Use verbatim; append only `?returnUrl=`. */
  loginUrl: string;
  /** Platform auth host for a console — NOT a per-tenant AUTH host. */
  authOrigin: string;
  /**
   * Payment return allowlist origin. OPTIONAL and absent for this console by
   * design — the API includes it only when a console needs a payment return
   * allowlist, and a product-admin console has no payment flow. Typed and
   * stored; read nowhere.
   */
  payOrigin?: string;
  deployEnv?: string;
}

/** Thrown by `fetchBootstrap` on a non-2xx response. Carries the numeric status so
 * the gate can special-case 404 (host not registered) from any other failure. */
export class BootstrapError extends Error {
  public readonly status: number;

  constructor(status: number) {
    super(`Bootstrap request failed with status ${status}`);
    this.name = 'BootstrapError';
    this.status = status;
  }
}

export async function fetchBootstrap(): Promise<PlatformBootstrap> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}/bootstrap`);
  } catch {
    throw new BootstrapError(0);
  }
  if (!res.ok) {
    throw new BootstrapError(res.status);
  }
  return (await res.json()) as PlatformBootstrap;
}

let bootstrap: PlatformBootstrap | null = null;

export function setBootstrap(value: PlatformBootstrap): void {
  bootstrap = value;
}

export function getBootstrap(): PlatformBootstrap {
  if (!bootstrap) {
    throw new Error('getBootstrap() called before bootstrap resolved');
  }
  return bootstrap;
}

/** Test seam. */
export function __resetBootstrap(): void {
  bootstrap = null;
}
